import { useMemo } from 'react';

import { useClientGroups } from '@/hooks/useClientGroups';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { useUserRole } from '@/hooks/useUserRole';

export interface ConversationListEntry {
  groupId: string;
  name: string;
  photoUrl?: string;
  preview: string;
  lastMsgAt: number;
  unread: number;
  isInboxArchived: boolean;
}

/**
 * Joins client groups with their unread/last-message info into a flat,
 * sorted conversation list — the same join ChatsPage._mock's `convos`
 * useMemo does, extracted so InboxList can reuse it without a full-page
 * dependency.
 *
 * Advisor-facing only: the "other party" per row is the client. A buyer/
 * owner-facing inbox would additionally need the advisor-profile join
 * ChatsPage does (name/photo aren't on ClientGroup for that direction) —
 * out of scope here since clients almost always have a single group and
 * skip the inbox list entirely (see ChatPanel's advisorViewMode logic).
 */
export const useConversationList = (tab: 'Open' | 'Archived') => {
  const { groups, isLoading: isLoadingGroups } = useClientGroups();
  const { groupUnreads, isLoading: isLoadingUnreads } = useUnreadMessages();
  const { isAdvisor } = useUserRole();

  const convos: ConversationListEntry[] = useMemo(() => {
    const showArchived = tab === 'Archived';
    return groups
      .filter(
        (g) => g.status === 'active' && (showArchived ? g.isInboxArchived : !g.isInboxArchived),
      )
      .map((g) => {
        const primaryMember =
          g.members.find((m) => m.role === 'Buyer' || m.role === 'Primary Owner') ?? g.members[0];
        const name = isAdvisor
          ? primaryMember?.name || g.holdingName || 'Client'
          : g.holdingName || 'Advisor';
        const photoUrl = isAdvisor ? primaryMember?.photoUrl : undefined;

        const unreadInfo = groupUnreads.find((u) => u.groupId === g.id);
        const recentMsgs = unreadInfo?.recentMessages;
        const lastMsg = recentMsgs?.[recentMsgs.length - 1];

        return {
          groupId: g.id,
          name,
          photoUrl,
          preview: lastMsg?.content?.slice(0, 60) ?? '',
          lastMsgAt: lastMsg?.createdAt ?? 0,
          unread: unreadInfo?.unreadCount ?? 0,
          isInboxArchived: g.isInboxArchived ?? false,
        };
      })
      .sort(
        (a, b) => b.lastMsgAt - a.lastMsgAt || b.unread - a.unread || a.name.localeCompare(b.name),
      );
  }, [groups, groupUnreads, isAdvisor, tab]);

  return { convos, isLoadingGroups, isLoadingUnreads };
};
